import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useMediaQuery, { PHONE } from '../hooks/useMediaQuery';
import { ArrowUpRight, X, ChevronDown, Github, ExternalLink } from 'lucide-react';

/* ─── Projects (strongest 6 first, remaining 6 collapsible) ───────────── */
const projects = [
  {
    id: 15,
    title: 'Chaos Lab',
    subtitle: 'Loughborough University · Interactive Research Demonstrator',
    category: 'Research / ML',
    tagline: 'How ML Models Fail Under Temporal & Distributional Shift',
    description:
      'Headline metrics can look fine while a model quietly breaks. Chaos Lab is an interactive demonstrator that makes that failure visible: performance, calibration and explanation stability, tracked as the data shifts underneath.',
    fullDescription:
      'An interactive research demonstrator exploring how machine-learning models can fail under temporal and distributional shift, even when conventional evaluation metrics appear acceptable. The project examines changes in predictive performance, calibration and explanation stability, with emphasis on the reliability of model interpretations in high-stakes decision contexts. Built to communicate robust and trustworthy ML concepts through reproducible experimentation and an accessible interactive interface.',
    problem:
      'Conventional evaluation metrics can look acceptable while a model\'s calibration and explanations degrade under shift. In high-stakes decisions, that hidden failure is the one that matters.',
    approach:
      'Reproducible experiments that apply temporal and distributional shift, then track predictive performance, calibration and explanation stability side by side in an interactive interface.',
    impact:
      'Turns abstract ideas about robust, trustworthy ML into something a non-specialist can explore hands-on. Deployed live as a companion to the MSc dissertation research.',
    demonstrates:
      'Robust ML, calibration analysis, explainability, experimental design, interactive product build, research communication.',
    tech: ['Python', 'Machine Learning', 'Distribution Shift', 'Calibration', 'Explainability', 'Interactive UI'],
    period: 'Jul 2026 – Sep 2026',
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=800&q=80',
    accentColor: '#22D3EE',
    liveUrl: 'https://chaos-lab-chi.vercel.app',
  },
  {
    id: 13,
    title: 'DemandSense-RX',
    subtitle: 'Udacity · AI Demand Forecasting & Inventory Optimisation',
    category: 'Product / ML',
    tagline: 'Demand Intelligence for Fulfilment Operations',
    description:
      'Demand uncertainty at scale is one of the hardest problems in supply chains. DemandSense-RX forecasts inventory needs and lets operators test stock allocation and replenishment decisions in a live Streamlit interface.',
    fullDescription:
      'Designed and deployed an AI-powered demand forecasting and inventory intelligence system. DemandSense-RX simulates real-world warehouse environments, such as Amazon-style fulfilment centres, enabling data-driven decisions on stock allocation, replenishment timing and demand volatility management. Positioned as a scalable prototype for enterprise deployment in retail, e-commerce and logistics.',
    problem:
      'Demand volatility makes stock allocation and replenishment timing hard to get right. Too much stock ties up capital; too little breaks fulfilment.',
    approach:
      'Built predictive demand models, modelled supply-chain dynamics (demand fluctuation, inventory constraints, fulfilment pressure) and wrapped them in a live Streamlit decision-support interface for scenario testing.',
    impact:
      'Translates machine-learning outputs into actionable logistics decisions. A working prototype that shows how ML applies to high-impact operations problems.',
    demonstrates:
      'Forecasting, operations analytics, simulation, decision-support product design, business translation of ML.',
    tech: ['Python', 'Forecasting', 'Streamlit', 'Inventory Modelling', 'Simulation'],
    period: 'Apr 2026 – Jul 2026',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80',
    accentColor: '#F59E0B',
    repoUrl: 'https://github.com/rayhanpatel88/DemandSense-RX',
  },
  {
    id: 1,
    title: 'TGF-XAI Framework',
    subtitle: 'MSc Dissertation',
    category: 'Research / ML',
    tagline: 'Temporal Generalisation & Explainability in Fraud Detection',
    description:
      'Fraud models fail silently. Distributions shift, accuracy drops, and no one knows why — or whether the model\'s explanations are still valid. This framework audits both at once, introducing a novel metric for regulators.',
    fullDescription:
      'Financial fraud detection models degrade as transaction patterns evolve. The standard response is to retrain. But retraining does not guarantee that the model\'s explanations remain consistent. Regulators under the EU AI Act require both performance and interpretability. The TGF-XAI Framework evaluates both in one pass, introducing the Explainability Stability Index (ESI) to quantify whether SHAP and LIME explanations stay stable as data drifts. Forward-chaining and rolling-window validation is applied across IEEE-CIS, NeurIPS Bank Account Fraud and Kaggle Western US Card Transactions, comparing logistic regression, random forest, XGBoost and deep learning models, building toward a drift-aware fraud detection prototype and an explainability monitoring dashboard.',
    problem:
      'Fraud models degrade silently under distributional shift. Existing evaluation methods treat performance and explainability as separate concerns. Neither flags when the other fails.',
    approach:
      'Designed a dual-evaluation framework combining temporal robustness testing with SHAP-based explainability stability analysis. Introduced the Explainability Stability Index (ESI) as a novel metric for quantifying explanation consistency over time.',
    impact:
      'Gives practitioners a single audit process that catches both performance decay and explanation drift. Directly applicable to EU AI Act compliance requirements for high-risk AI systems.',
    demonstrates:
      'Research rigour, ML engineering, XAI, temporal modelling, novel metric design, regulatory awareness.',
    tech: ['Python', 'XGBoost', 'PyTorch', 'SHAP', 'LIME', 'River', 'Scikit-learn'],
    period: 'Apr 2026 – May 2026',
    repoUrl: 'https://github.com/rayhanpatel88/fraud-xai-dissertation',
    image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=800&q=80',
    accentColor: '#9B3FFE',
  },
  {
    id: 2,
    title: 'Cancel Shield',
    subtitle: 'Etokify Product',
    category: 'Product / ML',
    tagline: 'Gym Membership Churn Prediction',
    description:
      'Gym operators lose members before they see it coming. This product scores every member by cancellation probability and surfaces who needs attention — and when. Delivered as a working prototype to a paying client.',
    fullDescription:
      'Built and delivered a churn prediction product for gym operators. The model scores members on cancellation likelihood using behavioural signals: visit frequency, tenure, and payment history. It surfaces at-risk members before they cancel. Delivered as a working prototype to a client through Etokify. The system turns reactive retention into something proactive.',
    problem:
      'Gym operators have no early warning system for cancellations. By the time a member leaves, the revenue is already lost. Retention interventions happen too late or not at all.',
    approach:
      'Built a supervised ML pipeline on behavioural data. Scored members by churn probability. Wrapped the output in an operator-facing interface so the right person gets flagged at the right time.',
    impact:
      'Live prototype delivered to a paying client. Demonstrated that churn intelligence is commercially viable for SME fitness operators. The path from model to business decision does not have to be long.',
    demonstrates:
      'End-to-end ML product delivery, client execution, churn modelling, commercial problem framing, startup velocity.',
    tech: ['Python', 'Scikit-learn', 'Pandas', 'SQL', 'Dashboard'],
    period: '2025 – 2026',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80',
    accentColor: '#00E5FF',
  },
  {
    id: 4,
    title: 'North Fresh Market',
    subtitle: 'Power BI Analytics',
    category: 'Data Analytics',
    tagline: 'Revenue Segmentation Dashboard',
    description:
      'A multinational retailer had the data but not the segmentation. The 35-44 age group was generating 27% of revenue from 19% of customers — an 8-point efficiency gap that was invisible until the model surfaced it.',
    fullDescription:
      'Designed and built a full analytics stack for a multinational grocery retailer in Power BI. Star schema data model, complex DAX measures, and layered dashboards covering revenue, demographics, and product performance. The key finding was not in the aggregate numbers. It was in the demographic segmentation that had been hiding in plain sight.',
    problem:
      'The retailer was making marketing and inventory decisions without demographic or product-level revenue segmentation. All revenue looked the same. It was not.',
    approach:
      'Designed a normalised star schema, built DAX measures for segmentation analysis, and created layered Power BI dashboards. The design prioritised clear segmentation over reporting volume.',
    impact:
      'The 35-44 demographic generated 27% of revenue from 19% of customers — an 8-point efficiency gap that directly changes where marketing budget should go. It was not visible before this work.',
    demonstrates:
      'BI engineering, data modelling, DAX, insight generation, commercial storytelling, stakeholder reporting.',
    tech: ['Power BI', 'DAX', 'SQL', 'Power Query', 'Star Schema'],
    period: '2025 – 2026',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
    accentColor: '#F59E0B',
  },
  {
    id: 6,
    title: 'BSc Dissertation: Tariff-Induced Inflation',
    subtitle: 'University of Westminster',
    category: 'Economics / Econometrics',
    tagline: 'U.S.–China Trade War Inflationary Transmission',
    description:
      '14,500 words. Three econometric methods. One finding that keeps recurring: the inflationary burden of U.S. tariffs on Chinese imports was not evenly distributed. It was sectoral, persistent, and regressive.',
    fullDescription:
      'Examined how U.S. Section 301 tariffs on Chinese imports (2018–2020) transmitted into domestic consumer prices across sectors and over time. Applied DiD, extended fixed-effects panel regression, and a distributed lag model to original sectoral CPI panels from BLS, WTO, CNBS, and FRED (2016–2024). Cross-national comparison showed U.S. electronics CPI rose 7.4pp more than China\'s, driven by China\'s exchange rate management, SOE pricing buffers, and centralised controls.',
    problem:
      'Standard trade war analysis treats tariff pass-through as uniform. It is not. Aggregate figures obscure which sectors and households bear the cost, and for how long.',
    approach:
      'DiD identified a 3.91 index point CPI differential in tariff-exposed sectors (p<0.001, R²=0.947). Fixed-effects panel showed each 1pp tariff increase raised CPI by 0.42 points. Distributed lag model found a cumulative 2-year inflation multiplier of 0.71.',
    impact:
      'Lowest-income households bore an effective burden 2.3x higher than top earners. Total long-run inflationary effect was more than double the contemporaneous impact. Tariff policy cannot be assessed without modelling distributional incidence over time.',
    demonstrates:
      'Econometric rigour, causal inference, panel data methods, distributional analysis, independent research design, academic writing.',
    tech: ['DiD', 'Fixed-Effects Panel', 'Distributed Lag', 'SPSS', 'BLS / FRED Data'],
    period: 'Sep 2024 – May 2025',
    image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
    accentColor: '#EF4444',
  },
  {
    id: 3,
    title: 'Ethical AI at Aviva',
    subtitle: 'Group Case Study',
    category: 'Strategy / AI Ethics',
    tagline: 'Competitive Advantage through Ethical AI',
    description:
      'AI regulation in financial services is tightening. The question is whether compliance becomes a burden or an advantage. This case study made the argument for the latter — with five prioritised interventions mapped to EIOPA and competitive positioning.',
    fullDescription:
      'Analysed how Aviva can treat ethical AI governance as a signal of trustworthiness rather than just a compliance cost. Applied PESTEL and Porter\'s Five Forces to map the regulatory landscape and identify interventions ranked by strategic impact. The output was a roadmap, not a checklist.',
    problem:
      'As EIOPA tightens AI regulation in financial services, insurers face a choice: treat compliance as overhead or position it as a signal of trustworthiness that competitors cannot easily replicate.',
    approach:
      'Applied PESTEL and Porter\'s Five Forces to assess the strategic landscape. Identified five AI governance interventions, each mapped to EIOPA requirements and competitive positioning opportunities.',
    impact:
      'Produced a strategic roadmap with clear prioritisation logic. Each recommendation was tied to both regulatory necessity and differentiation value, not one or the other.',
    demonstrates:
      'Strategic business analysis, AI ethics, regulatory intelligence, structured argumentation, executive communication.',
    tech: ['Strategic Analysis', 'EIOPA', 'PESTEL', "Porter's Five Forces"],
    period: 'Oct – Nov 2025',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
    accentColor: '#10B981',
  },
  {
    id: 9,
    title: 'Wage Discrimination Analysis',
    subtitle: 'University of Westminster',
    category: 'Statistics / Analytics',
    tagline: 'Regression Modelling of Pay Equity',
    description:
      'Applied multiple regression to a 382-employee dataset to investigate wage discrimination allegations. The model identified the legitimate predictors of salary — and the gaps that had no legitimate explanation. Adjusted R² = 0.646.',
    fullDescription:
      'Performed multiple regression analysis on a 382-employee dataset for Custom Woodworking, Inc. to investigate gender and ethnicity-based wage discrimination. Built and evaluated general and candidate regression models using SPSS, applying stepwise selection to identify the optimal model (Adjusted R² = 0.646). Conducted diagnostic checks including Durbin-Watson, P-P plot normality testing, and homoscedasticity verification. Key salary predictors identified: job level, age, experience, gender, union status, marital status, and race. Recommended structural HR reforms and pay transparency mechanisms to address identified inequities.',
    problem:
      'Pay discrimination allegations require more than a comparison of averages. The real challenge is isolating the effect of protected characteristics after controlling for legitimate salary drivers like job level, tenure, and experience.',
    approach:
      'Stepwise regression to identify the optimal predictor set. Full diagnostic checking to validate model assumptions. Separated legitimate salary variance from residual gaps attributable to gender and race.',
    impact:
      'Produced an evidence-based report with HR policy recommendations tied directly to the statistical findings. The model carries an Adjusted R² of 0.646, explaining 64.6% of salary variance, with the remainder pointing to potential structural inequity.',
    demonstrates:
      'Regression modelling, hypothesis testing, SPSS, diagnostic checking, statistical inference, evidence-based reporting.',
    tech: ['SPSS', 'Multiple Regression', 'Stepwise Selection', 'Durbin-Watson', 'Diagnostic Testing'],
    period: 'Jan – May 2024',
    image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80',
    accentColor: '#8B5CF6',
  },
  // ── Additional projects (hidden by default) ──
  {
    id: 16,
    title: 'Chaos Lab Stability Presentation',
    subtitle: 'Loughborough University · Research Presentation',
    category: 'Research / ML',
    tagline: 'Communicating Model Stability to Research Audiences',
    description:
      'The presentation companion to Chaos Lab: a structured walkthrough of why model stability matters and how shift erodes performance, calibration and explanations.',
    fullDescription:
      'A research presentation accompanying the Chaos Lab demonstrator, setting out the case for evaluating model stability, not just accuracy, under temporal and distributional shift.',
    problem:
      'Stability is a subtle idea. Without a clear narrative, audiences default to judging models on headline accuracy alone.',
    approach:
      'Paired the interactive Chaos Lab experiments with a structured presentation narrative covering performance, calibration and explanation stability.',
    impact:
      'Made the research accessible beyond the code, connecting experimental results to their implications for high-stakes decisions.',
    demonstrates:
      'Research communication, presentation design, technical storytelling.',
    tech: ['Research Communication', 'Model Stability', 'Presentation'],
    period: 'Aug 2026 – Sep 2026',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
    accentColor: '#38BDF8',
    liveUrl: 'https://chaos-lab-chi.vercel.app',
  },
  {
    id: 17,
    title: 'MSc Dissertation Report: Temporal Generalisation',
    subtitle: 'Loughborough University · MSc Data Science',
    category: 'Research / ML',
    tagline: 'The Full Write-Up of the TGF-XAI Research',
    description:
      'The dissertation report that brings the TGF-XAI research together: how fraud models generalise over time, and whether their explanations can still be trusted.',
    fullDescription:
      'The MSc Data Science dissertation report on temporal generalisation, consolidating the TGF-XAI framework, the proposed Explainability Stability Index and the experimental results into a formal research write-up.',
    problem:
      'Models are typically validated on random splits that ignore time, which overstates how well they will perform once deployed.',
    approach:
      'Formal write-up of temporal validation experiments and explanation-stability analysis across multiple fraud datasets and model families.',
    impact:
      'The capstone research output of the MSc, connecting robust ML evaluation to explainability and regulatory expectations.',
    demonstrates:
      'Research design, academic writing, temporal evaluation, explainability analysis.',
    tech: ['Temporal Generalisation', 'XAI', 'Fraud Detection', 'Academic Writing'],
    period: 'May 2026 – Sep 2026',
    image: 'https://images.unsplash.com/photo-1456324504439-367cee3b3c32?w=800&q=80',
    accentColor: '#A78BFA',
    repoUrl: 'https://github.com/rayhanpatel88/fraud-xai-dissertation',
  },
  {
    id: 5,
    title: 'Student Progress Dashboard',
    subtitle: 'Loughborough University',
    category: 'Data Engineering',
    tagline: 'Interactive Academic Performance Tracker in Streamlit',
    description:
      'Built a live, interactive dashboard in Streamlit to track academic performance, visualise grade trends over time, and surface key metrics in a clear interface. The output is functional, deployed, and publicly accessible.',
    fullDescription:
      'Designed and deployed an interactive student progress tracker using Python and Streamlit. The dashboard tracks academic performance across modules, visualises trends over time, and presents key metrics in a clean interface. Built to go beyond classroom exercises — this one is live and accessible at a public URL.',
    problem:
      'Academic progress data is typically locked in spreadsheets or institutional portals. There is no clean, interactive way to track performance trends and make data-driven study decisions in real time.',
    approach:
      'Built an end-to-end Python pipeline from data input to interactive visualisation using Streamlit. Focused on clarity of layout, metric visibility, and temporal trend analysis rather than feature volume.',
    impact:
      'Delivered a live, deployed application accessible publicly. Demonstrates the ability to take a data problem from concept to deployment, not just analysis in a notebook.',
    demonstrates:
      'Python, Streamlit, data visualisation, dashboard engineering, end-to-end deployment, practical application of analytics.',
    tech: ['Python', 'Streamlit', 'Pandas', 'Data Visualisation', 'Deployment'],
    period: 'Jan – Feb 2026',
    liveUrl: 'https://progressdashboard-l4hzbghqretheiwok4g49q.streamlit.app/',
    repoUrl: 'https://github.com/rayhanpatel88/progressdashboard',
    image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&q=80',
    accentColor: '#3B82F6',
  },
  {
    id: 7,
    title: 'Lufthansa Digital Transformation',
    subtitle: 'Loughborough University, MSc',
    category: 'Strategy / Digital Transformation',
    tagline: 'Data-Driven Competitive Advantage Analysis',
    description:
      'Analysed how Lufthansa Group uses AVIATAR and the Digital Hangar to strengthen its market position. The question was not what they built, but why those investments made strategic sense — and where the limits are.',
    fullDescription:
      'Applied RBV, the Big Data 5Vs framework, and POPIT to assess Lufthansa\'s digital capability stack, organisational risk exposure, and AI governance implications. The analysis moved beyond technology description into strategic logic: why these investments hold up, and under what conditions they do not.',
    problem:
      'Digital transformation reports typically catalogue what companies have done. The more useful question is whether those moves justified the cost or just added technology spend. Those are not the same thing.',
    approach:
      'Applied RBV to assess whether Lufthansa\'s digital assets are rare, inimitable, and non-substitutable. Used the Big Data 5Vs to evaluate data infrastructure maturity. POPIT surfaced the organisational and process risks that technology alone cannot resolve.',
    impact:
      'Produced a structured argument for which of Lufthansa\'s digital investments create lasting strategic value and which are table stakes. Delivered with executive summary, critical limitations, and sourced recommendations.',
    demonstrates:
      'Strategic frameworks, digital transformation analysis, AI governance reasoning, executive communication, academic rigour.',
    tech: ['RBV', 'Big Data 5Vs', 'POPIT', 'AI Governance', 'Strategic Analysis'],
    period: 'Nov 2025 – Jan 2026',
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
    accentColor: '#F97316',
  },
  {
    id: 8,
    title: 'Apple Digital Strategy',
    subtitle: 'University of Westminster',
    category: 'Digital Strategy',
    tagline: 'E-Commerce & Digital Marketing Framework',
    description:
      'A structured digital strategy report for Apple Inc., covering a SWOT of their e-commerce platform, an evaluation of three digital marketing channels, and enhancement proposals including AR, personalised recommendations, and trust signals.',
    fullDescription:
      'Developed a comprehensive digital strategy report for Apple Inc. as part of the Web-Enabled Business module. Conducted a SWOT analysis of Apple\'s e-commerce platform and evaluated three digital marketing channels: affiliate, organic, and influencer. Proposed platform enhancements including augmented reality product visualisation, algorithmic personalisation, and trust signals.',
    problem:
      'Apple\'s e-commerce platform is underutilised relative to its brand equity. The channel mix and on-site experience have not kept pace with what the data and technology now make possible.',
    approach:
      'Applied SWOT, OODA Loop, and PLY-Dash frameworks to assess current state and generate structured recommendations. Defined measurable e-commerce goals across digital services, wearables, and supply chain resilience.',
    impact:
      'Delivered a full strategic framework with defined KPIs and a customer journey map. Each recommendation was tied to a measurable outcome, not just a capability aspiration.',
    demonstrates:
      'Digital strategy, e-commerce analysis, marketing channel evaluation, KPI design, structured business argumentation.',
    tech: ['SWOT', 'OODA Loop', 'Customer Journey Mapping', 'KPI Design', 'E-Commerce Strategy'],
    period: 'Jan – May 2024',
    image: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=800&q=80',
    accentColor: '#A855F7',
  },
  {
    id: 10,
    title: 'Salary Discrimination Analysis',
    subtitle: 'University of Westminster',
    category: 'Statistics / Analytics',
    tagline: 'Statistical Investigation of Pay Disparity',
    description:
      'Statistical analysis of salary data for Manutan International\'s UK subsidiary. Hypothesis testing, correlation, and independent samples t-tests surfaced a gender pay gap: males earning ~£36,493 vs. females ~£24,452 on average.',
    fullDescription:
      'Conducted comprehensive statistical analysis of salary data for Manutan International\'s UK subsidiary to investigate gender and ethnicity-based wage discrimination. Applied hypothesis testing, Pearson correlation, chi-square tests, and independent samples t-tests using SPSS.',
    problem:
      'Summary salary figures can mask discrimination that only becomes visible through proper statistical disaggregation and significance testing. Averages lie. Controlled comparisons do not.',
    approach:
      'Multi-method statistical investigation: hypothesis testing, Pearson correlation, chi-square, and independent t-tests. Each method tested a different dimension of the discrimination allegation.',
    impact:
      'Demonstrated a statistically significant gender pay gap and produced evidence-based policy recommendations. The analysis showed that education and gender both independently predict salary.',
    demonstrates:
      'Hypothesis testing, correlation analysis, t-tests, chi-square, SPSS, statistical communication, policy recommendation.',
    tech: ['SPSS', 'Hypothesis Testing', 'Pearson Correlation', 'Chi-Square', 'Independent t-Test'],
    period: 'Sep – Dec 2023',
    image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80',
    accentColor: '#EC4899',
  },
  {
    id: 11,
    title: 'Youth Unemployment in Urban Morocco',
    subtitle: 'University of Westminster',
    category: 'Development Economics',
    tagline: 'Evidence-Based Policy for Urban Youth Employment',
    description:
      'Policy proposal addressing urban youth unemployment in Morocco, where over 40% of urban youth aged 15-24 are out of work. Seven interventions grounded in development economics theory and comparative international practice.',
    fullDescription:
      'Authored a 2,200-word policy proposal addressing urban youth unemployment in Morocco, focusing on Casablanca, Rabat, and Fes. Applied Human Capital Theory, Lewis Dual Sector Model, Dual Labour Market Theory, and Sen\'s Capability Approach to diagnose structural barriers.',
    problem:
      'Over 40% of urban youth in Morocco aged 15-24 are unemployed. Standard labour market interventions miss the structural barriers that reproduce the problem.',
    approach:
      'Applied four development economics frameworks to diagnose root causes. Comparative analysis of regional peers (Tunisia, Jordan) to identify transferable interventions.',
    impact:
      'Produced a structured seven-point policy framework with theory-grounded rationale for each intervention addressing both supply-side and demand-side labour market failures.',
    demonstrates:
      'Development economics, policy analysis, comparative case research, theoretical application, structured argumentation.',
    tech: ['Human Capital Theory', 'Lewis Dual Sector Model', "Sen's Capability Approach", 'Policy Design'],
    period: 'Feb – May 2025',
    image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80',
    accentColor: '#84CC16',
  },
  {
    id: 12,
    title: 'Morocco Country Development Profile',
    subtitle: 'University of Westminster',
    category: 'Development Economics',
    tagline: 'Macroeconomic Benchmarking 2013–2023',
    description:
      'A macroeconomic analysis of Morocco\'s development trajectory from 2013 to 2023, benchmarked against Algeria, Egypt, and Tunisia across nine key development indicators.',
    fullDescription:
      'Produced a 2,142-word development economics report analysing Morocco\'s economic trajectory from 2013 to 2023, benchmarked against Algeria, Egypt, and Tunisia. Applied the Lewis Dual Sector Model and Structural Transformation Theory.',
    problem:
      'Single-country development analysis misses comparative context. A single growth rate tells you almost nothing without benchmarking against regional peers across multiple dimensions.',
    approach:
      'Multi-indicator benchmarking across four North African economies (2013–2023). Applied Lewis Dual Sector and Structural Transformation Theory to explain divergences in HDI, inequality, and sectoral composition.',
    impact:
      'Produced a comparative development profile with policy recommendations grounded in structural theory, surfacing Morocco\'s specific vulnerabilities that aggregate growth figures obscure.',
    demonstrates:
      'Development economics, cross-country benchmarking, multi-indicator analysis, structural theory application, academic writing.',
    tech: ['Lewis Dual Sector Model', 'Structural Transformation Theory', 'HDI', 'Gini Analysis'],
    period: 'Jan – Mar 2025',
    image: 'https://images.unsplash.com/photo-1526779259212-939e64788e3c?w=800&q=80',
    accentColor: '#14B8A6',
  },

  {
    id: 18,
    title: 'Portfolio Website',
    subtitle: 'OP. Growth Marketing · Design & Development',
    category: 'Design / Development',
    tagline: 'This Site',
    description:
      'The site you are on. A React portfolio with motion design, structured case studies, and searchable experience and credential archives.',
    fullDescription:
      'A modern portfolio built with React, Tailwind and Framer Motion on the front end, with a Python and SQL backend, designed to make a broad body of work easy to explore.',
    problem:
      'A wide range of roles, research and credentials needs a structure that recruiters can scan in seconds.',
    approach:
      'Component-driven React build with motion design, filterable case studies, a searchable experience timeline and a grouped credential archive.',
    impact:
      'One place to explore the work, verify credentials and book a call.',
    demonstrates:
      'Front-end engineering, UI/UX design, information architecture.',
    tech: ['React', 'JavaScript', 'Python', 'SQL', 'Framer Motion'],
    period: '2026',
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&q=80',
    accentColor: '#E879F9',
    liveUrl: 'https://www.rayhanpatel.world',
  },
];

const INITIAL_COUNT = 8;
const EASE = [0.16, 1, 0.3, 1];

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'ai', label: 'AI & ML' },
  { id: 'strategy', label: 'Strategy' },
  { id: 'economics', label: 'Economics' },
  { id: 'analytics', label: 'Analytics & Build' },
];

const groupOf = (category) => {
  if (/ML/.test(category)) return 'ai';
  if (/Econom/.test(category)) return 'economics';
  if (/Strategy/.test(category)) return 'strategy';
  return 'analytics';
};

const numbered = projects.map((p, i) => ({
  ...p,
  index: String(i + 1).padStart(2, '0'),
  group: groupOf(p.category),
}));

/* Responsive Unsplash sources — phones fetch ~480px, retina desktops up to 1200px */
const srcSet = (url) => {
  const base = url.split('?')[0];
  return [480, 800, 1200].map((w) => `${base}?w=${w}&q=75&auto=format ${w}w`).join(', ');
};

/* ─── Spotlight hover (CSS vars, no re-render) ────────────────────────── */
const trackSpotlight = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--sx', `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty('--sy', `${e.clientY - r.top}px`);
};

/* ─── Project Card ────────────────────────────────────────────────────── */
const ProjectCard = ({ project, featured, onClick }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 28 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.97 }}
    transition={{ duration: 0.55, ease: EASE }}
    onClick={onClick}
    onMouseMove={trackSpotlight}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onClick())}
    role="button"
    tabIndex={0}
    aria-label={`Open case study: ${project.title}`}
    className={`project-card group relative rounded-[22px] border overflow-hidden cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
      featured ? 'md:col-span-2 md:grid md:grid-cols-[1.15fr_1fr]' : 'flex flex-col'
    }`}
    style={{ '--accent': project.accentColor }}
    data-testid={`project-card-${project.id}`}
  >
    <div className={`relative overflow-hidden ${featured ? 'aspect-[16/10] md:aspect-auto md:min-h-[360px]' : 'aspect-[16/10]'}`}>
      <img
        src={project.image}
        srcSet={srcSet(project.image)}
        sizes={featured ? '(min-width: 1024px) 45vw, (min-width: 768px) 55vw, 100vw' : '(min-width: 1024px) 30vw, (min-width: 768px) 50vw, 100vw'}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
      />
      <div className={`absolute inset-0 ${featured ? 'bg-gradient-to-t md:bg-gradient-to-r from-[#0b0b0f] via-[#0b0b0f]/30 to-transparent md:from-transparent md:via-transparent md:to-[#0b0b0f]' : 'bg-gradient-to-t from-[#0b0b0f] via-[#0b0b0f]/30 to-transparent'}`} />
      <div className="absolute inset-0 mix-blend-color opacity-40" style={{ background: project.accentColor }} />
      <div className="absolute top-4 left-4 flex items-center gap-2">
        <span className="project-pill">
          <span className="font-display" style={{ color: project.accentColor }}>{project.index}</span>
          <span className="w-px h-3 bg-white/20" />
          {project.category}
        </span>
        {project.liveUrl && (
          <span className="project-pill text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]" />
            Live
          </span>
        )}
      </div>
      <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white flex items-center justify-center opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]">
        <ArrowUpRight className="w-4 h-4 text-black" />
      </div>
    </div>

    <div className={`relative flex flex-col flex-1 ${featured ? 'p-7 md:p-10 justify-center' : 'p-6'}`}>
      {featured && (
        <span className="text-[9.5px] uppercase tracking-[0.3em] font-medium mb-4" style={{ color: project.accentColor }}>
          Featured · {project.period}
        </span>
      )}
      <span className="text-[10.5px] text-text-secondary tracking-wide block mb-2">{project.subtitle}</span>
      <h3 className={`font-display font-medium tracking-tight leading-[1.1] mb-3 ${featured ? 'text-3xl md:text-[2.4rem]' : 'text-xl md:text-[1.3rem]'}`}>
        {project.title}
      </h3>
      <p className={`text-text-secondary leading-relaxed mb-5 ${featured ? 'text-[0.95rem] max-w-[46ch]' : 'text-sm line-clamp-3'}`}>
        {project.description}
      </p>
      <div className="mt-auto flex flex-wrap items-center gap-1.5">
        {project.tech.slice(0, featured ? 5 : 3).map((tech) => (
          <span key={tech} className="px-2.5 py-1 text-[11px] rounded-md text-white/60 bg-white/[0.045] border border-white/[0.05]">
            {tech}
          </span>
        ))}
        {project.tech.length > (featured ? 5 : 3) && (
          <span className="px-1.5 py-1 text-[11px] text-text-secondary">
            +{project.tech.length - (featured ? 5 : 3)}
          </span>
        )}
      </div>
    </div>
  </motion.article>
);

/* ─── Project Modal ───────────────────────────────────────────────────── */
const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-6 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={project.title}
      data-testid="project-modal"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />

      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.98 }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-[28px] sm:rounded-[28px] border border-white/10 bg-[#0b0b0f] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]"
      >
        <button
          onClick={onClose}
          className="sticky top-4 float-right mr-4 z-10 p-2.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-colors"
          data-testid="project-modal-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative aspect-[16/8] -mt-[52px]">
          <img src={project.image} srcSet={srcSet(project.image)} sizes="(min-width: 768px) 768px, 100vw" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0f] via-[#0b0b0f]/40 to-transparent" />
          <div className="absolute inset-0 mix-blend-color opacity-40" style={{ background: project.accentColor }} />
          <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${project.accentColor}, transparent)` }} />
        </div>

        <div className="relative px-6 sm:px-7 md:px-10 md:pb-10 -mt-16 safe-bottom">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[10px] uppercase tracking-[0.25em] font-medium" style={{ color: project.accentColor }}>
              {project.index}
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-text-secondary">{project.category}</span>
          </div>
          <h2 className="font-display text-3xl md:text-[2.6rem] leading-[1.05] font-medium tracking-tight mb-2">
            {project.title}
          </h2>
          <p className="text-text-secondary text-sm mb-6">
            {project.subtitle} &middot; {project.period}
          </p>

          {(project.liveUrl || project.repoUrl) && (
            <div className="flex flex-wrap gap-2.5 mb-8">
              {project.liveUrl && (
                <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-white/90 transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> Live demo
                </a>
              )}
              {project.repoUrl && (
                <a href={project.repoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 bg-white/[0.04] text-sm font-medium hover:bg-white/[0.08] hover:border-white/30 transition-colors">
                  <Github className="w-3.5 h-3.5" /> Source code
                </a>
              )}
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-3 mb-8">
            {[
              { label: 'Problem', body: project.problem },
              { label: 'Approach', body: project.approach },
              { label: 'Impact', body: project.impact },
            ].map(({ label, body }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.07, duration: 0.5, ease: EASE }}
                className="p-5 rounded-2xl border border-white/[0.07] bg-white/[0.025]"
              >
                <p className="text-[9px] uppercase tracking-[0.28em] font-medium mb-2.5" style={{ color: project.accentColor }}>
                  {label}
                </p>
                <p className="text-[13px] text-text-secondary leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-[1rem] leading-[1.75] text-white/85 mb-7">{project.fullDescription}</p>

          <div className="mb-7 p-5 rounded-2xl border border-white/[0.07] bg-white/[0.02]">
            <p className="text-[9px] uppercase tracking-[0.28em] text-text-secondary font-medium mb-2">
              What this demonstrates
            </p>
            <p className="text-sm text-white/80 leading-relaxed">{project.demonstrates}</p>
          </div>

          <p className="text-[10px] uppercase tracking-[0.25em] text-text-secondary font-medium mb-3">
            Technologies &amp; Methods
          </p>
          <div className="flex flex-wrap gap-2">
            {project.tech.map((tech) => (
              <span key={tech} className="px-3 py-1.5 text-[13px] rounded-full bg-white/[0.06] border border-white/[0.08]">
                {tech}
              </span>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ─── Section ─────────────────────────────────────────────────────────── */
const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const isPhone = useMediaQuery(PHONE);
  const initialCount = isPhone ? 4 : INITIAL_COUNT;

  const filtered = filter === 'all' ? numbered : numbered.filter((p) => p.group === filter);
  const visible = showAll ? filtered : filtered.slice(0, initialCount);
  const counts = Object.fromEntries(
    FILTERS.map((f) => [f.id, f.id === 'all' ? numbered.length : numbered.filter((p) => p.group === f.id).length])
  );

  return (
    <section id="projects" className="relative py-20 sm:py-28 md:py-40" data-testid="projects-section">
      <div className="absolute top-0 left-0 right-0 h-px section-divider" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(124,58,237,0.07), transparent 70%)' }} />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.75, ease: EASE }}
          className="mb-10 md:mb-12"
        >
          <div className="section-label mb-6">Selected Work</div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none font-medium">
              Projects &amp;<br />
              <span className="gradient-text-brand">Case Studies</span>
            </h2>
            <p className="text-text-secondary text-sm max-w-[300px] leading-relaxed pb-1 sm:text-right">
              {numbered.length} projects across ML research, econometrics, strategy and analytics.
              Each structured as a problem, an approach and a result.
            </p>
          </div>
        </motion.div>

        {/* Filter bar */}
        <div className="mb-8 -mx-6 px-6 overflow-x-auto no-scrollbar">
          <div className="inline-flex gap-1 p-1 rounded-full border border-white/[0.08] bg-white/[0.025] backdrop-blur-md">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => { setFilter(f.id); setShowAll(false); }}
                className={`relative px-4 py-2 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-colors duration-300 ${
                  filter === f.id ? 'text-black' : 'text-text-secondary hover:text-white'
                }`}
                aria-pressed={filter === f.id}
              >
                {filter === f.id && (
                  <motion.span layoutId="project-filter" className="absolute inset-0 rounded-full bg-white" transition={{ type: 'spring', stiffness: 380, damping: 32 }} />
                )}
                <span className="relative z-10 flex items-center gap-1.5">
                  {f.label}
                  <span className={`text-[10px] tabular-nums ${filter === f.id ? 'text-black/50' : 'text-white/30'}`}>{counts[f.id]}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {visible.map((project, i) => (
              <ProjectCard
                key={project.id}
                project={project}
                featured={i === 0}
                onClick={() => setSelectedProject(project)}
              />
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length > initialCount && (
          <div className="flex justify-center mt-12">
            <motion.button
              onClick={() => setShowAll((v) => !v)}
              className="group flex items-center gap-2 px-8 py-3.5 border border-white/[0.12] rounded-full text-sm font-medium text-text-secondary hover:text-white hover:bg-white/[0.04] hover:border-white/[0.28] transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              {showAll ? 'Show fewer projects' : `View all ${filtered.length} projects`}
              <motion.span animate={{ rotate: showAll ? 180 : 0 }} transition={{ duration: 0.3 }} className="flex">
                <ChevronDown className="w-4 h-4" />
              </motion.span>
            </motion.button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedProject && (
          <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
